import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, Thermometer, Bell } from 'lucide-react';
import { apiBase, authHeaders, resolveOrgId } from '../../utils/authHeaders';
import { listAll } from '../../capture/photoQueue';
import { canDo, useExperienceFor } from '../../experience/experience';
import { useMobileAuth } from '../MobileAuthProvider';

/**
 * Shared mobile UI states (A14): one page header, one empty state, one error
 * state with retry, one loading skeleton. Used where the swap is mechanical.
 */
export function PageHeader({ title, subtitle = null, action = null }) {
  return (
    <header
      className="sticky top-0 z-20 bg-surface border-b border-surface-border px-4 py-3 flex items-center justify-between"
      data-testid="mobile-page-header"
    >
      <div className="min-w-0">
        <h1 className="font-display text-base text-ink truncate">{title}</h1>
        {subtitle && <p className="text-xs text-ink-muted truncate">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

export function EmptyState({ title, hint = null, testid = 'mobile-empty' }) {
  return (
    <div className="px-4 py-10 text-center" data-testid={testid}>
      <p className="text-sm text-ink">{title}</p>
      {hint && <p className="text-xs text-ink-muted mt-1">{hint}</p>}
    </div>
  );
}

export function ErrorState({ message, onRetry, testid = 'mobile-error' }) {
  return (
    <div className="px-4 py-8 text-center" data-testid={testid}>
      <p className="text-sm text-danger">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`${testid}-retry`}
          className="mt-3 underline min-h-[44px]"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function LoadingSkeleton({ rows = 3, testid = 'mobile-loading' }) {
  return (
    <div className="px-4 py-3 space-y-3" data-testid={testid} aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded bg-surface-sunken animate-pulse" />
      ))}
    </div>
  );
}

/**
 * A one-time, dismissible iOS Safari "Add to Home Screen" hint. Shown only when
 * the app is NOT already installed (no standalone display mode) and only on iOS,
 * because that is the only platform where the instruction is needed.
 */
export const IOS_HINT_KEY = 'mobile_ios_install_hint_dismissed';

export function isIosSafari(userAgent, { standalone = false } = {}) {
  if (standalone) return false;
  const ua = String(userAgent || '');
  const isIos = /iPad|iPhone|iPod/.test(ua) && !/Windows Phone/.test(ua);
  // Chrome/Firefox on iOS cannot add to the home screen the same way.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

export function IosInstallHint({ storage = null } = {}) {
  const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
  const [dismissed, setDismissed] = useState(() => {
    try { return store ? store.getItem(IOS_HINT_KEY) === '1' : true; } catch { return true; }
  });

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const standalone = typeof window !== 'undefined'
    && (window.navigator?.standalone === true
      || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches));

  if (dismissed || !isIosSafari(ua, { standalone })) return null;

  return (
    <div
      className="mx-4 mt-3 rounded-lg border border-surface-border bg-surface-muted px-3 py-2 text-sm text-ink"
      data-testid="ios-install-hint"
      role="note"
    >
      <p>
        To keep this on your home screen: tap <strong>Share</strong>, then
        <strong> Add to Home Screen</strong>.
      </p>
      <button
        type="button"
        className="mt-1 underline min-h-[44px]"
        data-testid="ios-install-hint-dismiss"
        onClick={() => {
          try { store?.setItem(IOS_HINT_KEY, '1'); } catch { /* private mode */ }
          setDismissed(true);
        }}
      >
        Got it
      </button>
    </div>
  );
}

/**
 * Home: what needs attention first. Every count comes from an endpoint that
 * already exists — no new ones. Checklists are asked for only when the server's
 * experience says this person may read them (`actions['safety.read']`: the
 * role and the food-safety module), never by catching the 403.
 */
export function useAttentionItems(exp) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const answered = exp.status !== 'loading';
  const readsSafety = exp.status === 'ready' && canDo(exp, 'safety.read');

  useEffect(() => {
    if (!answered) return undefined;
    let cancelled = false;
    const orgId = resolveOrgId();
    const siteId = typeof localStorage !== 'undefined' ? localStorage.getItem('current_location_id') : null;

    const headers = authHeaders({ orgId, locationId: siteId, json: true });
    const getJson = async (path) => {
      try {
        const res = await fetch(`${apiBase()}${path}`, { headers });
        return res.ok ? await res.json() : null;
      } catch { return null; } // offline: skip this one rather than failing the screen
    };

    (async () => {
      // Independent sources, fetched together so a slow one does not hold up the rest.
      const [invoices, rows, safety, unread] = await Promise.all([
        getJson('/api/documents/invoices?state=awaiting_approval'),
        listAll().catch(() => null), // failed photo uploads, from the queue on this device
        readsSafety ? getJson('/api/mobile/safety/checklists') : null,
        getJson('/api/mobile/notifications/unread-count'),
      ]);
      const out = [];
      if (invoices) out.push({ key: 'invoices', label: 'Invoices waiting', count: (invoices.items || []).length, to: '/mobile/invoices' });
      if (rows) out.push({ key: 'uploads', label: 'Failed uploads', count: rows.filter((r) => r.status === 'failed').length, to: '/mobile/receiving' });
      if (safety) {
        const overdue = (safety.checklists || []).filter((c) => c.overdue === true).length;
        out.push({ key: 'temps', label: 'Temperature checks due', count: overdue, to: '/mobile/boh/temperature' });
      }
      if (unread) out.push({ key: 'alerts', label: 'Unread alerts', count: Number(unread.unread_count) || 0, to: '/mobile/notifications' });

      if (cancelled) return;
      if (out.length === 0) setError('Could not load what needs attention.');
      else setItems(out);
    })();

    return () => { cancelled = true; };
  }, [answered, readsSafety]);

  return { items, error };
}

export function AttentionList() {
  const { token } = useMobileAuth();
  const { items, error } = useAttentionItems(useExperienceFor(token));
  if (error) return <ErrorState message={error} />;
  if (!items) return <LoadingSkeleton rows={4} testid="attention-loading" />;

  const needsAttention = items.filter((i) => i.count > 0);
  if (needsAttention.length === 0) {
    return <EmptyState title="Nothing needs your attention." testid="attention-empty" />;
  }
  return (
    <ul className="divide-y divide-surface-border" data-testid="attention-list">
      {needsAttention.map((item) => (
        <li key={item.key}>
          <Link
            to={item.to}
            className="flex items-center justify-between px-4 py-3 min-h-[44px] bg-surface"
            data-testid={`attention-${item.key}`}
          >
            <span className="text-sm text-ink">{item.label}</span>
            <span className="text-sm font-medium text-ink tabular-nums">{item.count}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export const ATTENTION_ICONS = { FileText, Upload, Thermometer, Bell };
